import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { KioskService } from 'app/core/services/kiosk.service';

/**
 * CanMatch guard that keeps a KIOSK display (the 42" panel) on the kiosk
 * (`/kiosk`) surface, mirroring how `mobileRedirectGuard` keeps phones on
 * `/m/*`.
 *
 * Kiosk mode is an explicit persisted flag (see `KioskService`), set via the
 * sign-in toggle or the `?kiosk=1` URL param. When the flag is on and the
 * user is authenticated and tries to match a non-kiosk app route, it is
 * diverted to `/kiosk` BEFORE the desktop layout / resolver / page mount, so
 * the panel only ever shows kiosk pages.
 *
 * When the flag is off (the default for landscape desktops, PWAs, phones)
 * routes match normally — this guard is a no-op.
 */
export const kioskRedirectGuard: CanMatchFn = (
    _route: Route,
    segments: UrlSegment[],
) => {
    const router = inject(Router);
    const kiosk = inject(KioskService);

    // Kiosk mode off → behave as if this guard weren't here.
    if (!kiosk.isKiosk) {
        return true;
    }

    const attemptedPath = '/' + segments.map((s) => s.path).join('/');

    // Routes that must stay reachable on the kiosk: the kiosk surface
    // itself plus the auth / public flows (so the user can sign in, sign
    // out, unlock, or open a public resource link).
    const skipRedirectPaths = [
        '/kiosk',
        '/sign-in',
        '/sign-up',
        '/sign-out',
        '/forgot-password',
        '/reset-password',
        '/unlock-session',
        '/confirmation-required',
        '/resource-view',
    ];
    if (skipRedirectPaths.some((path) => attemptedPath.includes(path))) {
        return true;
    }

    // Only redirect authenticated users; everyone else signs in first.
    if (typeof localStorage === 'undefined') {
        return true;
    }
    const userData = localStorage.getItem('userData');
    if (!userData || userData === 'null' || userData === 'undefined') {
        return true;
    }

    console.log(
        `🖥️ Kiosk mode enabled, diverting "${attemptedPath}" to "/kiosk"`,
    );

    return router.parseUrl('/kiosk');
};
